import { useState, FormEvent, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, AlertTriangle } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import { DatePicker } from '@/components/DatePicker'

type Account = { id: number; code: string; name: string; account_type: string; is_active: boolean }

type StatementLine = {
  id: number
  statement: number
  transaction_date: string
  description: string
  reference: string
  amount: string
  matched_journal_line: number | null
  matched_journal_entry_number: string | null
  matched_journal_entry_description: string | null
  is_matched: boolean
  notes: string
  sort_order: number
}

type Statement = {
  id: number
  bank_account: number
  bank_account_code: string
  bank_account_name: string
  statement_date: string
  opening_balance: string
  closing_balance: string
  notes: string
  is_reconciled: boolean
  reconciled_at: string | null
  matched_count: number
  unmatched_count: number
  line_total: string
  lines: StatementLine[]
}

type CandidateJL = {
  id: number
  date: string
  entry_number: string
  entry_description: string
  partner_name: string | null
  description: string
  amount: string
}

export default function FinanceBankRec() {
  const qc = useQueryClient()

  const accountsQ = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/finance/accounts/')).data,
  })
  const statementsQ = useQuery({
    queryKey: ['finance', 'bank-statements'],
    queryFn: async () => (await api.get<Statement[]>('/finance/bank-statements/')).data,
  })

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)

  const bankAccounts = (accountsQ.data || []).filter((a) =>
    a.is_active && a.account_type === 'ASSET' && a.code.startsWith('11'))
  const statements = statementsQ.data || []
  const selected = statements.find((s) => s.id === selectedId) || null

  const createStmt = useMutation({
    mutationFn: async (payload: any) => (await api.post('/finance/bank-statements/', payload)).data,
    onSuccess: (s: Statement) => {
      qc.invalidateQueries({ queryKey: ['finance', 'bank-statements'] })
      setSelectedId(s.id)
      setCreating(false)
    },
  })
  const deleteStmt = useMutation({
    mutationFn: async (id: number) => api.delete(`/finance/bank-statements/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'bank-statements'] })
      setSelectedId(null)
    },
  })
  const reconcileStmt = useMutation({
    mutationFn: async ({ id, mark }: { id: number; mark: boolean }) =>
      (await api.post(`/finance/bank-statements/${id}/${mark ? 'mark_reconciled' : 'mark_unreconciled'}/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance', 'bank-statements'] }),
  })

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Bank Reconciliation</h2>
          <p className="text-sm text-gray-500">Match bank statement lines against journal entries.</p>
        </div>
        <Button onClick={() => setCreating(true)} disabled={bankAccounts.length === 0}>
          + New statement
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Statement list */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide border-b">
              Statements ({statements.length})
            </div>
            {statementsQ.isLoading ? (
              <p className="p-4 text-sm text-gray-500">Loading…</p>
            ) : statements.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No statements yet.</p>
            ) : (
              <div className="divide-y divide-gray-200">
                {statements.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedId(s.id)}
                    className={`w-full text-left p-3 hover:bg-gray-50 ${selectedId === s.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                  >
                    <div className="text-sm font-medium text-gray-800">
                      {s.bank_account_code} {s.bank_account_name}
                    </div>
                    <div className="text-xs text-gray-500">{s.statement_date}</div>
                    <div className="text-xs mt-1 flex items-center space-x-2">
                      {s.is_reconciled && (
                        <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                          <Check className="h-3 w-3" /> Reconciled
                        </span>
                      )}
                      <span className="text-gray-500">{s.matched_count}/{s.matched_count + s.unmatched_count} matched</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Detail view */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500 text-sm">
              Select or create a statement to start reconciling.
            </div>
          ) : (
            <StatementDetail
              statement={selected}
              onReconcile={(mark) => reconcileStmt.mutate({ id: selected.id, mark })}
              onDelete={() => {
                if (confirm(`Delete statement for ${selected.bank_account_code} @ ${selected.statement_date}?`))
                  deleteStmt.mutate(selected.id)
              }}
            />
          )}
        </div>
      </div>

      {creating && (
        <NewStatementDialog
          bankAccounts={bankAccounts}
          submitting={createStmt.isPending}
          error={createStmt.error as any}
          onCancel={() => setCreating(false)}
          onSubmit={(p) => createStmt.mutate(p)}
        />
      )}
    </AdminLayout>
  )
}

// ────────────────────────────────────────
// Detail view: statement lines + matching
// ────────────────────────────────────────

function StatementDetail({
  statement,
  onReconcile,
  onDelete,
}: {
  statement: Statement
  onReconcile: (mark: boolean) => void
  onDelete: () => void
}) {
  const qc = useQueryClient()
  const candidatesQ = useQuery({
    queryKey: ['finance', 'bank-statements', statement.id, 'candidates'],
    queryFn: async () =>
      (await api.get<CandidateJL[]>(`/finance/bank-statements/${statement.id}/candidate_journal_lines/`)).data,
  })

  const [addingLine, setAddingLine] = useState(false)

  const matchMut = useMutation({
    mutationFn: async ({ line_id, journal_line_id }: { line_id: number; journal_line_id: number }) =>
      (await api.post(`/finance/bank-statement-lines/${line_id}/match/`, { journal_line_id })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'bank-statements'] })
      qc.invalidateQueries({ queryKey: ['finance', 'bank-statements', statement.id, 'candidates'] })
    },
  })
  const unmatchMut = useMutation({
    mutationFn: async (line_id: number) =>
      (await api.post(`/finance/bank-statement-lines/${line_id}/unmatch/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'bank-statements'] })
      qc.invalidateQueries({ queryKey: ['finance', 'bank-statements', statement.id, 'candidates'] })
    },
  })
  const addLineMut = useMutation({
    mutationFn: async (payload: any) =>
      (await api.post('/finance/bank-statement-lines/', { ...payload, statement: statement.id })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'bank-statements'] })
      setAddingLine(false)
    },
  })
  const deleteLineMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/finance/bank-statement-lines/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance', 'bank-statements'] }),
  })

  const candidates = candidatesQ.data || []

  // Find best matching candidates for each unmatched statement line: same amount, close date
  const candidateForLine = (line: StatementLine): CandidateJL | undefined => {
    const lineAmt = Number(line.amount)
    return candidates.find((c) => Math.abs(Number(c.amount) - lineAmt) < 0.005)
  }

  const matchedSum = useMemo(
    () => statement.lines.filter((l) => l.is_matched).reduce((s, l) => s + Number(l.amount), 0),
    [statement.lines]
  )
  const unmatchedSum = useMemo(
    () => statement.lines.filter((l) => !l.is_matched).reduce((s, l) => s + Number(l.amount), 0),
    [statement.lines]
  )
  const expectedClosing = Number(statement.opening_balance) + Number(statement.line_total)
  const closingDiff = Number(statement.closing_balance) - expectedClosing

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-800">
              {statement.bank_account_code} {statement.bank_account_name}
            </h3>
            <p className="text-xs text-gray-500">Statement date: {statement.statement_date}</p>
          </div>
          <div className="space-x-2">
            {statement.is_reconciled ? (
              <button onClick={() => onReconcile(false)}
                className="text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded">Mark un-reconciled</button>
            ) : (
              <button onClick={() => onReconcile(true)}
                disabled={statement.unmatched_count > 0}
                className="text-sm px-3 py-1 bg-green-600 disabled:bg-gray-300 hover:bg-green-700 text-white rounded">
                Mark reconciled
              </button>
            )}
            <button onClick={onDelete} className="text-sm px-3 py-1 text-red-600 hover:bg-red-50 rounded">
              Delete
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Stat label="Opening" value={statement.opening_balance} />
          <Stat label="Movements" value={statement.line_total} />
          <Stat label="Expected close" value={expectedClosing.toFixed(2)} />
          <Stat
            label="Reported close"
            value={statement.closing_balance}
            highlight={Math.abs(closingDiff) > 0.005 ? 'warn' : 'ok'}
          />
        </div>
        {Math.abs(closingDiff) > 0.005 && (
          <p className="mt-2 text-xs text-amber-700 inline-flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> Closing differs by {closingDiff.toFixed(2)} from opening + movements
          </p>
        )}
      </div>

      {/* Lines */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Lines ({statement.lines.length}) — {statement.matched_count} matched, {statement.unmatched_count} unmatched
          </span>
          {!statement.is_reconciled && (
            <button onClick={() => setAddingLine(true)} className="text-sm text-blue-600 hover:underline">
              + Add line
            </button>
          )}
        </div>

        {statement.lines.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">No lines yet. Add the bank transactions from this statement.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-right w-28">Amount</th>
                <th className="px-3 py-2 text-left">Matched journal entry</th>
                <th className="px-3 py-2 w-32">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {statement.lines.map((l) => (
                <tr key={l.id} className={l.is_matched ? 'bg-green-50/30' : ''}>
                  <td className="px-3 py-2 text-xs">{l.transaction_date}</td>
                  <td className="px-3 py-2">
                    <div>{l.description}</div>
                    {l.reference && <div className="text-xs text-gray-500 font-mono">{l.reference}</div>}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono ${Number(l.amount) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {Number(l.amount).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {l.is_matched ? (
                      <div>
                        <span className="font-mono text-gray-700">{l.matched_journal_entry_number}</span>
                        <div className="text-gray-500">{l.matched_journal_entry_description}</div>
                      </div>
                    ) : (
                      (() => {
                        const c = candidateForLine(l)
                        return c ? (
                          <button
                            onClick={() => matchMut.mutate({ line_id: l.id, journal_line_id: c.id })}
                            disabled={matchMut.isPending}
                            className="text-blue-600 hover:underline"
                          >
                            Match {c.entry_number} ({c.entry_description.slice(0, 30)})
                          </button>
                        ) : (
                          <span className="text-gray-400 italic">no match</span>
                        )
                      })()
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {l.is_matched ? (
                      <button onClick={() => unmatchMut.mutate(l.id)}
                        className="text-amber-700 hover:underline text-xs">Unmatch</button>
                    ) : (
                      !statement.is_reconciled && (
                        <button onClick={() => {
                          if (confirm('Delete this line?')) deleteLineMut.mutate(l.id)
                        }} className="text-red-600 hover:underline text-xs">Delete</button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 text-sm">
              <tr>
                <td colSpan={2} className="px-3 py-1 text-right text-gray-600">Matched total</td>
                <td className="px-3 py-1 text-right font-mono">{matchedSum.toFixed(2)}</td>
                <td colSpan={2}></td>
              </tr>
              <tr>
                <td colSpan={2} className="px-3 py-1 text-right text-gray-600">Unmatched total</td>
                <td className="px-3 py-1 text-right font-mono">{unmatchedSum.toFixed(2)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {addingLine && (
        <AddLineDialog
          submitting={addLineMut.isPending}
          error={addLineMut.error as any}
          defaultDate={statement.statement_date}
          onCancel={() => setAddingLine(false)}
          onSubmit={(p) => addLineMut.mutate(p)}
        />
      )}
    </div>
  )
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: 'ok' | 'warn' }) {
  return (
    <div>
      <div className="text-xs uppercase text-gray-500 tracking-wide">{label}</div>
      <div className={`text-lg font-mono ${
        highlight === 'warn' ? 'text-amber-700' : highlight === 'ok' ? 'text-green-700' : 'text-gray-800'
      }`}>
        {Number(value).toFixed(2)}
      </div>
    </div>
  )
}

function NewStatementDialog({
  bankAccounts, onSubmit, onCancel, submitting, error,
}: {
  bankAccounts: Account[]
  onSubmit: (p: any) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const [form, setForm] = useState({
    bank_account: bankAccounts[0]?.id ?? 0,
    statement_date: new Date().toISOString().slice(0, 10),
    opening_balance: '0',
    closing_balance: '0',
    notes: '',
  })
  const setField = <K extends keyof typeof form>(k: K, v: typeof form[K]) =>
    setForm((f) => ({ ...f, [k]: v }))
  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(form) }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">New statement</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldLegend>Statement</FieldLegend>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="stmt-bank-account">Bank account</FieldLabel>
                <SearchableSelect
                  id="stmt-bank-account"
                  value={form.bank_account ? String(form.bank_account) : undefined}
                  onChange={(v) => setField('bank_account', Number(v))}
                  placeholder="— select —"
                  options={bankAccounts.map((a) => ({
                    value: String(a.id), label: a.name, hint: a.code,
                  }))}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="stmt-date">Statement date (period end)</FieldLabel>
                <DatePicker
                  id="stmt-date" required value={form.statement_date}
                  onChange={(v) => setField('statement_date', v)}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="stmt-opening">Opening balance</FieldLabel>
                  <Input
                    id="stmt-opening"
                    type="number" step="0.01" value={form.opening_balance}
                    onChange={(e) => setField('opening_balance', e.target.value)}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="stmt-closing">Closing balance</FieldLabel>
                  <Input
                    id="stmt-closing"
                    type="number" step="0.01" value={form.closing_balance}
                    onChange={(e) => setField('closing_balance', e.target.value)}
                  />
                </Field>
              </div>
              <Field>
                <FieldLabel htmlFor="stmt-notes">Notes</FieldLabel>
                <Textarea
                  id="stmt-notes"
                  value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  rows={2}
                />
              </Field>

              {error && (
                <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded">
                  {String(error?.response?.data ? JSON.stringify(error.response.data) : error.message || error)}
                </div>
              )}
            </FieldGroup>
          </FieldSet>

          <div className="flex justify-end gap-2 pt-3">
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create statement'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function AddLineDialog({
  submitting, error, defaultDate, onSubmit, onCancel,
}: {
  submitting: boolean
  error: any
  defaultDate: string
  onSubmit: (p: any) => void
  onCancel: () => void
}) {
  const [form, setForm] = useState({
    transaction_date: defaultDate,
    description: '', reference: '', amount: '0', notes: '',
  })
  const set = <K extends keyof typeof form>(k: K, v: typeof form[K]) =>
    setForm((f) => ({ ...f, [k]: v }))
  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(form) }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Add statement line</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldLegend>Statement line</FieldLegend>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="line-date">Date</FieldLabel>
                <DatePicker
                  id="line-date" required value={form.transaction_date}
                  onChange={(v) => set('transaction_date', v)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="line-description">Description</FieldLabel>
                <Input
                  id="line-description"
                  type="text" value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  placeholder="e.g. Customer payment XYZ"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="line-reference">Reference</FieldLabel>
                <Input
                  id="line-reference"
                  type="text" value={form.reference}
                  onChange={(e) => set('reference', e.target.value)}
                  placeholder="Bank transaction ID"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="line-amount">Amount (negative for outflows)</FieldLabel>
                <Input
                  id="line-amount"
                  type="number" step="0.01" required value={form.amount}
                  onChange={(e) => set('amount', e.target.value)}
                  className="font-mono"
                />
              </Field>

              {error && (
                <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded">
                  {String(error?.response?.data ? JSON.stringify(error.response.data) : error.message || error)}
                </div>
              )}
            </FieldGroup>
          </FieldSet>

          <div className="flex justify-end gap-2 pt-3">
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Adding…' : 'Add line'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
